import { extendDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `account-team` of the console gateway — over `apps/api/src/routes/account-team/` (design §27.9.2). Owned by
 * plan-014 unit **B2**.
 *
 *   readAccountProfile          `getAccountProfile`               profile + the Overview's workspace metadata
 *   changeAccountProfile        `updateAccountProfile`            name, default language (If-Match)
 *   scheduleBillingTimezone     `scheduleAccountBillingTimezone`  from the next billing month (If-Match)
 *   readAccountMembers          `listAccountMembers`              everyone with access
 *   changeAccountMemberRole     `updateAccountMember`             (If-Match)
 *   removeAccountMember         `removeAccountMember`             (If-Match)
 *   readAccountInvitations      `listAccountInvitations`          every state
 *   sendAccountInvitation       `createAccountInvitation`
 *   resendAccountInvitation     `resendAccountInvitation`         the old link dies
 *   revokeAccountInvitation     `revokeAccountInvitation`
 *   transferAccountOwnership    `transferAccountOwnership`        step-up (answers `step_up_required` without one)
 *   inspectAccountInvitation    `inspectAccountInvitation`        the accept page's first read
 *   acceptAccountInvitation     `acceptAccountInvitation`
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in B2's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated
 * by the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through
 * {@link operationsOf}, a structural view of the client with exactly these methods (C1's precedent): it compiles
 * before and after the regeneration, and a client that still lacks one raises naming the regeneration — never an
 * empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import type { AccountRole } from "@wetalk/domain";

import type { ConsoleGateway, WorkspaceRoleView } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface AccountWorkspaceMetadataView {
  readonly workspaceId: string;
  readonly name: string;
  readonly timeZone: string;
  readonly archived: boolean;
  readonly memberCount: number;
}

export interface AccountProfileView {
  readonly accountId: string;
  readonly name: string;
  readonly state: string;
  readonly country: string;
  readonly defaultLanguageCode: string | null;
  readonly billingTimezone: string | null;
  readonly pendingBillingTimezone: string | null;
  readonly pendingBillingTimezoneFrom: string | null;
  readonly revision: number;
  readonly workspace: readonly AccountWorkspaceMetadataView[];
  readonly personCount: number;
  readonly accountRoleCount: number;
  /** `null` unless the person may read the organization's money. */
  readonly balanceCount: number | null;
}

export type InvitableAccountRole = Exclude<AccountRole, "owner">;

export interface TeamWorkspaceRoleView {
  readonly workspaceId: string;
  readonly workspaceName: string | null;
  readonly memberRole: WorkspaceRoleView;
}

export interface AccountMemberView {
  readonly personId: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly emailVerified: boolean;
  /** `null` for someone with workspace roles only. */
  readonly accountMembershipId: string | null;
  readonly accountRole: AccountRole | null;
  readonly revision: number | null;
  readonly workspaceRole: readonly TeamWorkspaceRoleView[];
  readonly signIn: { readonly password: boolean; readonly authenticator: boolean; readonly external: readonly ("google" | "microsoft")[] };
  readonly lastSeenAt: string | null;
}

export type AccountInvitationState = "pending" | "accepted" | "expired" | "revoked" | "superseded";

export interface AccountInvitationView {
  readonly accountInvitationId: string;
  readonly email: string;
  readonly accountRole: AccountRole | null;
  readonly workspaceRole: readonly TeamWorkspaceRoleView[];
  readonly state: AccountInvitationState;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly invitedByName: string | null;
  readonly invitedByOperator: boolean;
  readonly revokedAt: string | null;
  readonly revokedByName: string | null;
  readonly acceptedAt: string | null;
}

export interface AccountInvitationOfferView {
  readonly email: string;
  readonly accountRole: InvitableAccountRole | null;
  readonly workspaceRole: readonly { readonly workspaceId: string; readonly memberRole: WorkspaceRoleView }[];
}

export interface SentAccountInvitationView {
  readonly invitation: AccountInvitationView;
  /** `email` when it was queued; `share_link` while no email sender is bound — then `invitationPath` is set, once. */
  readonly delivery: "email" | "share_link";
  readonly invitationPath: string | null;
}

export type InvitationLandingView =
  | { readonly kind: "workspace"; readonly workspaceId: string }
  | { readonly kind: "finance" }
  | { readonly kind: "organization" };

export interface InvitationLinkView {
  readonly accountId: string;
  readonly accountName: string;
  readonly email: string;
  readonly invitedByName: string | null;
  readonly expiresAt: string;
  readonly state: "pending" | "accepted";
  readonly accountRole: AccountRole | null;
  readonly workspaceRole: readonly (TeamWorkspaceRoleView & { readonly available: boolean })[];
  readonly requiresAuthenticator: boolean;
  readonly signInExists: boolean;
  readonly viewer: { readonly email: string; readonly emailVerified: boolean; readonly matches: boolean } | null;
  readonly landing: InvitationLandingView | null;
}

export interface AcceptedInvitationView {
  readonly accountId: string;
  readonly landing: InvitationLandingView;
  readonly replayed: boolean;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The organization's profile with the Overview's workspace metadata and counts. */
    readAccountProfile(accountId: string): Promise<AccountProfileView>;
    /** Renames the organization or changes its default language; `revision` is the one read. */
    changeAccountProfile(
      accountId: string,
      change: { readonly name?: string; readonly defaultLanguageCode?: string },
      revision: number,
    ): Promise<AccountProfileView>;
    /** Changes the billing timezone from the next billing month; naming the zone in force withdraws a pending one. */
    scheduleBillingTimezone(accountId: string, timeZone: string, revision: number): Promise<void>;
    /** Everyone with access to the organization. */
    readAccountMembers(accountId: string): Promise<readonly AccountMemberView[]>;
    /** Changes someone's organization role (never to Owner — that is a transfer). */
    changeAccountMemberRole(accountId: string, accountMembershipId: string, accountRole: AccountRole, revision: number): Promise<void>;
    /** Removes someone's organization role (their workspace roles stay). */
    removeAccountMember(accountId: string, accountMembershipId: string, revision: number): Promise<void>;
    /** The organization's invitations, every state, newest first. */
    readAccountInvitations(accountId: string): Promise<readonly AccountInvitationView[]>;
    /** Invites someone with exactly these roles. */
    sendAccountInvitation(accountId: string, offer: AccountInvitationOfferView): Promise<SentAccountInvitationView>;
    /** Resends with a fresh link; the earlier link stops working. */
    resendAccountInvitation(accountId: string, accountInvitationId: string): Promise<SentAccountInvitationView>;
    revokeAccountInvitation(accountId: string, accountInvitationId: string): Promise<void>;
    /** Transfers ownership; answers `step_up_required` (401) until the person confirmed it is them. */
    transferAccountOwnership(
      accountId: string,
      toPersonId: string,
      formerOwnerRole: InvitableAccountRole,
      idempotencyKey: string,
    ): Promise<void>;
    /** What an invitation link offers (the token is the proof; nobody needs to be signed in). */
    inspectAccountInvitation(token: string): Promise<InvitationLinkView>;
    /** Accepts it as the signed-in person; the console then switches into the organization. */
    acceptAccountInvitation(token: string): Promise<AcceptedInvitationView>;
  }
}

export type AccountTeamGatewaySlice = Pick<
  ConsoleGateway,
  | "readAccountProfile"
  | "changeAccountProfile"
  | "scheduleBillingTimezone"
  | "readAccountMembers"
  | "changeAccountMemberRole"
  | "removeAccountMember"
  | "readAccountInvitations"
  | "sendAccountInvitation"
  | "resendAccountInvitation"
  | "revokeAccountInvitation"
  | "transferAccountOwnership"
  | "inspectAccountInvitation"
  | "acceptAccountInvitation"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in B2's fragments. */
export const accountTeamGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

type Header = Readonly<Record<string, string>>;

interface AccountTeamOperations {
  getAccountProfile(request: { path: { account_id: string } }): Promise<ApiResult>;
  updateAccountProfile(request: { path: { account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  scheduleAccountBillingTimezone(request: { path: { account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  listAccountMembers(request: { path: { account_id: string } }): Promise<ApiResult>;
  updateAccountMember(request: { path: { account_id: string; account_membership_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  removeAccountMember(request: { path: { account_id: string; account_membership_id: string }; header?: Header }): Promise<ApiResult>;
  listAccountInvitations(request: { path: { account_id: string } }): Promise<ApiResult>;
  createAccountInvitation(request: { path: { account_id: string }; body?: unknown }): Promise<ApiResult>;
  resendAccountInvitation(request: { path: { account_id: string; account_invitation_id: string } }): Promise<ApiResult>;
  revokeAccountInvitation(request: { path: { account_id: string; account_invitation_id: string } }): Promise<ApiResult>;
  transferAccountOwnership(request: { path: { account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  inspectAccountInvitation(request: { body?: unknown }): Promise<ApiResult>;
  acceptAccountInvitation(request: { body?: unknown }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): AccountTeamOperations {
  const held = client as unknown as Partial<Record<keyof AccountTeamOperations, unknown>>;
  return new Proxy({} as AccountTeamOperations, {
    get(_target, name: string) {
      const found = held[name as keyof AccountTeamOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_d1a92f99", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const ACCOUNT_ROLE_SET: ReadonlySet<string> = new Set(["owner", "admin", "finance", "viewer"]);
const WORKSPACE_ROLE_SET: ReadonlySet<string> = new Set(["owner", "admin", "member", "viewer", "developer", "campaign_manager", "billing"]);
const INVITATION_STATE_SET: ReadonlySet<string> = new Set(["pending", "accepted", "expired", "revoked", "superseded"]);

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function accountRoleOrNull(value: unknown, path: string): AccountRole | null {
  if (value === null || value === undefined) return null;
  const role = readText(value, path);
  if (!ACCOUNT_ROLE_SET.has(role)) throw new Error(`${path}: "${role}" is not an organization role`);
  return role as AccountRole;
}

function workspaceRoleOf(value: unknown, path: string): WorkspaceRoleView {
  const role = readText(value, path);
  if (!WORKSPACE_ROLE_SET.has(role)) throw new Error(`${path}: "${role}" is not a workspace role`);
  return role as WorkspaceRoleView;
}

function teamWorkspaceRoleOf(value: unknown, path: string): TeamWorkspaceRoleView {
  const one = readRecord(value, path);
  return {
    workspaceId: readText(one["workspace_id"], `${path}.workspace_id`),
    workspaceName: textOrNull(one["workspace_name"], `${path}.workspace_name`),
    memberRole: workspaceRoleOf(one["member_role"], `${path}.member_role`),
  };
}

export function accountProfileOfWire(data: Readonly<Record<string, unknown>>): AccountProfileView {
  const workspace = data["workspace"] === undefined ? [] : readArray(data["workspace"], "getAccountProfile.workspace");
  const balance = data["balance_count"];
  return {
    accountId: readText(data["account_id"], "account_id"),
    name: readText(data["name"], "name"),
    state: readText(data["state"], "state"),
    country: readText(data["country"], "country"),
    defaultLanguageCode: textOrNull(data["default_language_code"], "default_language_code"),
    billingTimezone: textOrNull(data["billing_timezone"], "billing_timezone"),
    pendingBillingTimezone: textOrNull(data["pending_billing_timezone"], "pending_billing_timezone"),
    pendingBillingTimezoneFrom: textOrNull(data["pending_billing_timezone_from"], "pending_billing_timezone_from"),
    revision: readWholeNumber(data["revision"], "revision"),
    workspace: workspace.map((value, index) => {
      const path = `workspace[${index}]`;
      const one = readRecord(value, path);
      return {
        workspaceId: readText(one["workspace_id"], `${path}.workspace_id`),
        name: readText(one["name"], `${path}.name`),
        timeZone: readText(one["time_zone"], `${path}.time_zone`),
        archived: readBoolean(one["archived"], `${path}.archived`),
        memberCount: readWholeNumber(one["member_count"], `${path}.member_count`),
      };
    }),
    personCount: data["person_count"] === undefined ? 0 : readWholeNumber(data["person_count"], "person_count"),
    accountRoleCount: data["account_role_count"] === undefined ? 0 : readWholeNumber(data["account_role_count"], "account_role_count"),
    balanceCount: balance === null || balance === undefined ? null : readWholeNumber(balance, "balance_count"),
  };
}

export function accountMemberOfWire(value: unknown, index: number): AccountMemberView {
  const path = `listAccountMembers.data[${String(index)}]`;
  const one = readRecord(value, path);
  const person = readRecord(one["person"], `${path}.person`);
  const signIn = readRecord(one["sign_in_summary"], `${path}.sign_in_summary`);
  return {
    personId: readText(person["person_id"], `${path}.person.person_id`),
    displayName: textOrNull(person["display_name"], `${path}.person.display_name`),
    email: textOrNull(person["email"], `${path}.person.email`),
    emailVerified: readBoolean(person["email_verified"], `${path}.person.email_verified`),
    accountMembershipId: textOrNull(one["account_membership_id"], `${path}.account_membership_id`),
    accountRole: accountRoleOrNull(one["account_role"], `${path}.account_role`),
    revision: one["revision"] === null ? null : readWholeNumber(one["revision"], `${path}.revision`),
    workspaceRole: readArray(one["workspace_role"], `${path}.workspace_role`).map((each, at) =>
      teamWorkspaceRoleOf(each, `${path}.workspace_role[${at}]`),
    ),
    signIn: {
      password: readBoolean(signIn["password"], `${path}.sign_in_summary.password`),
      authenticator: readBoolean(signIn["authenticator"], `${path}.sign_in_summary.authenticator`),
      external: readArray(signIn["external"], `${path}.sign_in_summary.external`).map((each) => {
        const kind = readText(each, `${path}.sign_in_summary.external`);
        if (kind !== "google" && kind !== "microsoft") throw new Error(`${path}: "${kind}" is not a sign-in provider`);
        return kind;
      }),
    },
    lastSeenAt: textOrNull(one["last_seen_at"], `${path}.last_seen_at`),
  };
}

export function accountInvitationOfWire(value: unknown, path: string): AccountInvitationView {
  const one = readRecord(value, path);
  const state = readText(one["state"], `${path}.state`);
  if (!INVITATION_STATE_SET.has(state)) throw new Error(`${path}: "${state}" is not an invitation state`);
  const by = readRecord(one["invited_by"], `${path}.invited_by`);
  return {
    accountInvitationId: readText(one["account_invitation_id"], `${path}.account_invitation_id`),
    email: readText(one["email"], `${path}.email`),
    accountRole: accountRoleOrNull(one["account_role"], `${path}.account_role`),
    workspaceRole: readArray(one["workspace_role"], `${path}.workspace_role`).map((each, at) =>
      teamWorkspaceRoleOf(each, `${path}.workspace_role[${at}]`),
    ),
    state: state as AccountInvitationState,
    expiresAt: readText(one["expires_at"], `${path}.expires_at`),
    createdAt: readText(one["created_at"], `${path}.created_at`),
    invitedByName: textOrNull(by["display_name"], `${path}.invited_by.display_name`),
    invitedByOperator: readBoolean(by["by_operator"], `${path}.invited_by.by_operator`),
    revokedAt: textOrNull(one["revoked_at"], `${path}.revoked_at`),
    revokedByName: textOrNull(one["revoked_by_name"], `${path}.revoked_by_name`),
    acceptedAt: textOrNull(one["accepted_at"], `${path}.accepted_at`),
  };
}

function sentOfWire(data: Readonly<Record<string, unknown>>, operationId: string): SentAccountInvitationView {
  const delivery = readText(data["delivery"], `${operationId}.delivery`);
  if (delivery !== "email" && delivery !== "share_link") throw new Error(`${operationId}: "${delivery}" is not a delivery`);
  return {
    invitation: accountInvitationOfWire(data["invitation"], `${operationId}.invitation`),
    delivery,
    invitationPath: textOrNull(data["invitation_path"], `${operationId}.invitation_path`),
  };
}

function landingOfWire(value: unknown, path: string): InvitationLandingView {
  const one = readRecord(value, path);
  const kind = readText(one["kind"], `${path}.kind`);
  if (kind === "workspace") return { kind, workspaceId: readText(one["workspace_id"], `${path}.workspace_id`) };
  if (kind === "finance" || kind === "organization") return { kind };
  throw new Error(`${path}: "${kind}" is not a landing`);
}

export function invitationLinkOfWire(data: Readonly<Record<string, unknown>>): InvitationLinkView {
  const organization = readRecord(data["organization"], "inspect.organization");
  const offered = readRecord(data["offered"], "inspect.offered");
  const policy = readRecord(data["policy"], "inspect.policy");
  const viewer = data["viewer"];
  const state = readText(data["state"], "inspect.state");
  if (state !== "pending" && state !== "accepted") throw new Error(`inspect: "${state}" is not a link state`);
  return {
    accountId: readText(organization["account_id"], "inspect.organization.account_id"),
    accountName: readText(organization["name"], "inspect.organization.name"),
    email: readText(data["email"], "inspect.email"),
    invitedByName: textOrNull(data["invited_by_name"], "inspect.invited_by_name"),
    expiresAt: readText(data["expires_at"], "inspect.expires_at"),
    state,
    accountRole: accountRoleOrNull(offered["account_role"], "inspect.offered.account_role"),
    workspaceRole: readArray(offered["workspace_role"], "inspect.offered.workspace_role").map((each, at) => {
      const path = `inspect.offered.workspace_role[${at}]`;
      const one = readRecord(each, path);
      return extendDisplayView(teamWorkspaceRoleOf(each, path), { available: readBoolean(one["available"], `${path}.available`) });
    }),
    requiresAuthenticator: readBoolean(policy["requires_authenticator"], "inspect.policy.requires_authenticator"),
    signInExists: readBoolean(data["sign_in_exists"], "inspect.sign_in_exists"),
    viewer:
      viewer === null || viewer === undefined
        ? null
        : (() => {
            const one = readRecord(viewer, "inspect.viewer");
            return {
              email: readText(one["email"], "inspect.viewer.email"),
              emailVerified: readBoolean(one["email_verified"], "inspect.viewer.email_verified"),
              matches: readBoolean(one["matches"], "inspect.viewer.matches"),
            };
          })(),
    landing: data["landing"] === null || data["landing"] === undefined ? null : landingOfWire(data["landing"], "inspect.landing"),
  };
}

const ifMatch = (revision: number): Header => ({ "if-match": `"${String(revision)}"` });

export function accountTeamApiSlice(client: WeTalkApiClient, _context: SliceContext): AccountTeamGatewaySlice {
  const api = (): AccountTeamOperations => operationsOf(client);
  return {
    async readAccountProfile(accountId) {
      return accountProfileOfWire(dataOf(await api().getAccountProfile({ path: { account_id: accountId } }), "getAccountProfile"));
    },
    async changeAccountProfile(accountId, change, revision) {
      const body = {
        ...(change.name === undefined ? {} : { name: change.name }),
        ...(change.defaultLanguageCode === undefined ? {} : { default_language_code: change.defaultLanguageCode }),
      };
      return accountProfileOfWire(
        dataOf(await api().updateAccountProfile({ path: { account_id: accountId }, body, header: ifMatch(revision) }), "updateAccountProfile"),
      );
    },
    async scheduleBillingTimezone(accountId, timeZone, revision) {
      assertOk(
        await api().scheduleAccountBillingTimezone({
          path: { account_id: accountId },
          body: { billing_timezone: timeZone },
          header: ifMatch(revision),
        }),
      );
    },
    async readAccountMembers(accountId) {
      const body = readRecord(assertOk(await api().listAccountMembers({ path: { account_id: accountId } })), "listAccountMembers");
      return readArray(body["data"], "listAccountMembers.data").map(accountMemberOfWire);
    },
    async changeAccountMemberRole(accountId, accountMembershipId, accountRole, revision) {
      assertOk(
        await api().updateAccountMember({
          path: { account_id: accountId, account_membership_id: accountMembershipId },
          body: { account_role: accountRole },
          header: ifMatch(revision),
        }),
      );
    },
    async removeAccountMember(accountId, accountMembershipId, revision) {
      assertOk(
        await api().removeAccountMember({ path: { account_id: accountId, account_membership_id: accountMembershipId }, header: ifMatch(revision) }),
      );
    },
    async readAccountInvitations(accountId) {
      const body = readRecord(assertOk(await api().listAccountInvitations({ path: { account_id: accountId } })), "listAccountInvitations");
      return readArray(body["data"], "listAccountInvitations.data").map((each, index) =>
        accountInvitationOfWire(each, `listAccountInvitations.data[${String(index)}]`),
      );
    },
    async sendAccountInvitation(accountId, offer) {
      const body = {
        email: offer.email,
        account_role: offer.accountRole,
        workspace_role: offer.workspaceRole.map((one) => ({ workspace_id: one.workspaceId, member_role: one.memberRole })),
      };
      return sentOfWire(dataOf(await api().createAccountInvitation({ path: { account_id: accountId }, body }), "createAccountInvitation"), "createAccountInvitation");
    },
    async resendAccountInvitation(accountId, accountInvitationId) {
      return sentOfWire(
        dataOf(
          await api().resendAccountInvitation({ path: { account_id: accountId, account_invitation_id: accountInvitationId } }),
          "resendAccountInvitation",
        ),
        "resendAccountInvitation",
      );
    },
    async revokeAccountInvitation(accountId, accountInvitationId) {
      assertOk(await api().revokeAccountInvitation({ path: { account_id: accountId, account_invitation_id: accountInvitationId } }));
    },
    async transferAccountOwnership(accountId, toPersonId, formerOwnerRole, idempotencyKey) {
      assertOk(
        await api().transferAccountOwnership({
          path: { account_id: accountId },
          body: { to_person_id: toPersonId, former_owner_role: formerOwnerRole },
          header: { "idempotency-key": idempotencyKey },
        }),
      );
    },
    async inspectAccountInvitation(token) {
      return invitationLinkOfWire(dataOf(await api().inspectAccountInvitation({ body: { token } }), "inspectAccountInvitation"));
    },
    async acceptAccountInvitation(token) {
      const data = dataOf(await api().acceptAccountInvitation({ body: { token } }), "acceptAccountInvitation");
      return {
        accountId: readText(data["account_id"], "acceptAccountInvitation.account_id"),
        landing: landingOfWire(data["landing"], "acceptAccountInvitation.landing"),
        replayed: readBoolean(data["replayed"], "acceptAccountInvitation.replayed"),
      };
    },
  };
}
